/**
 * Extract the next speakable chunk from sanitized TTS text at safe boundaries.
 * Punctuation is excluded from spoken chunks; Anam pauses at chunk boundaries.
 */

export const EARLY_BREAK_MIN_CHARS = 40;
export const FORCE_FLUSH_MAX_CHARS = 120;

const ABBREVIATIONS = new Set([
  'dr',
  'mr',
  'mrs',
  'ms',
  'prof',
  'sr',
  'jr',
  'vs',
  'etc',
  'e.g',
  'i.e',
  'u.s',
  'u.k',
  'a.m',
  'p.m',
]);

export interface ExtractSpeakableChunkOptions {
  /** Break at comma/semicolon sooner so the first chunk plays quickly. */
  preferEarlyBreak?: boolean;
  /** Stream finished — flush any remaining text as the final chunk. */
  forceFlush?: boolean;
}

export interface SpeakableChunkResult {
  chunk: string;
  remainder: string;
}

function trimLeadingWhitespace(text: string): string {
  return text.replace(/^\s+/, '');
}

function isAbbreviationDot(text: string, dotIndex: number): boolean {
  const before = text.slice(0, dotIndex).trimEnd();
  const token = before.split(/\s+/).pop()?.replace(/\.$/, '').toLowerCase() ?? '';
  if (ABBREVIATIONS.has(token)) return true;
  // Single-letter initials: "A." in "A. Smith"
  if (/^[a-z]$/i.test(token)) return true;
  return false;
}

function isDecimalOrCurrencyDot(text: string, dotIndex: number): boolean {
  const before = text[dotIndex - 1];
  const after = text[dotIndex + 1];
  if (!/\d/.test(before) || !/\d/.test(after)) return false;
  // Currency prefix like $19.99
  let i = dotIndex - 1;
  while (i >= 0 && /\d/.test(text[i])) i -= 1;
  if (i >= 0 && /[$€£¥]/.test(text[i])) return true;
  return /\d/.test(before) && /\d/.test(after);
}

function hasPartialEmail(text: string): boolean {
  const atIndex = text.lastIndexOf('@');
  if (atIndex === -1) return false;
  const afterAt = text.slice(atIndex + 1);
  if (/\s/.test(afterAt)) return false;
  // Complete email: user@domain.tld
  if (/^[^\s@]+\.[^\s@]+/.test(afterAt)) return false;
  return true;
}

function isSentenceBoundary(text: string, index: number): boolean {
  const ch = text[index];
  if (ch !== '.' && ch !== '?' && ch !== '!') return false;
  if (ch === '.' && (isAbbreviationDot(text, index) || isDecimalOrCurrencyDot(text, index))) {
    return false;
  }
  const next = text[index + 1];
  return next === undefined || /\s/.test(next);
}

function findSentenceBoundary(text: string): number {
  for (let i = 0; i < text.length; i += 1) {
    if (isSentenceBoundary(text, i)) return i;
  }
  return -1;
}

function findEarlyBreak(text: string): number {
  if (text.length < EARLY_BREAK_MIN_CHARS) return -1;
  for (let i = EARLY_BREAK_MIN_CHARS; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === ',' || ch === ';') {
      const next = text[i + 1];
      if (next === undefined || /\s/.test(next)) return i;
    }
  }
  return -1;
}

function findWordBoundaryFlush(text: string): number {
  if (text.length < FORCE_FLUSH_MAX_CHARS) return -1;
  for (let i = FORCE_FLUSH_MAX_CHARS; i >= EARLY_BREAK_MIN_CHARS; i -= 1) {
    if (/\s/.test(text[i])) return i;
  }
  return -1;
}

function splitAtBoundary(text: string, boundaryIndex: number): SpeakableChunkResult | null {
  const chunk = text.slice(0, boundaryIndex).trimEnd();
  const remainder = trimLeadingWhitespace(text.slice(boundaryIndex + 1));
  if (!chunk) return null;
  return { chunk, remainder };
}

/**
 * Returns the next speakable chunk, or null if more text is needed.
 */
export function extractSpeakableChunk(
  pendingSanitized: string,
  opts: ExtractSpeakableChunkOptions = {},
): SpeakableChunkResult | null {
  const pending = pendingSanitized.trimStart();
  if (!pending) return null;
  if (!opts.forceFlush && hasPartialEmail(pending)) return null;

  const sentenceIdx = findSentenceBoundary(pending);
  if (sentenceIdx >= 0) {
    return splitAtBoundary(pending, sentenceIdx);
  }

  if (opts.preferEarlyBreak) {
    const earlyIdx = findEarlyBreak(pending);
    if (earlyIdx >= 0) {
      return splitAtBoundary(pending, earlyIdx);
    }
    const wordIdx = findWordBoundaryFlush(pending);
    if (wordIdx >= 0) {
      return splitAtBoundary(pending, wordIdx);
    }
  }

  if (opts.forceFlush) {
    const trimmed = pending.trim();
    if (trimmed) return { chunk: trimmed, remainder: '' };
  }

  return null;
}
